import { createDraftSafeSelector } from "@reduxjs/toolkit";
import { StepperIndexes } from "../../stepperIndexes";
import { RootState } from "../rootStore";

export const createSelector = createDraftSafeSelector.withTypes<RootState>();

export const stepperStateSelector = (state: RootState) => state.stepper;
export const selectExistingFundsRows = (state: RootState) => state.stepper.existingFundRows;
export const selectNewFundsRows = (state: RootState) => state.stepper.newFundRows;
export const selectSmsStepState = (state: RootState) => state.stepper.smsStepState;
export const currentStepIndexSelector = (state: RootState) => state.stepper.stepIndex;
export const editInvestmentStrategyResultSelector = (state: RootState) => state.stepper.editInvestmentStrategyResult;
export const whatToChangeAnswersSelector = (state: RootState) => state.stepper.whatToChangeAnswers;
export const questionnaireRejectedSelector = (state: RootState) => state.stepper.questionnaireRejected;
export const canChangeInvestmentStrategySelector = (state: RootState) => state.stepper.canChangeInvestmentStrategy;
export const sustainabilityIdSelector = (state: RootState) => state.stepper.sustainabilityId;
export const allStepListSelector = (state: RootState) => state.stepper.allStepsList;
export const changeContributionStateSelector = (state: RootState) => state.stepper.changeContributionState;
export const preselectedContributionSelector = (state: RootState) => state.stepper.preselectedContribution;
export const stepperTypeSelector = (state: RootState) => state.stepper.stepperType;
export const stepperContractIdSelector = (state: RootState) => state.stepper.contractId;
export const stepperInitializationErrorCodeSelector = (state: RootState) => state.stepper.initializationErrorCode;
export const amlIdentificationStateSelector = (state: RootState) => state.stepper.AMLIdentificationState;
export const questionnaireSubmitDateSelector = (state: RootState) => state.stepper.lastQuestionnaireSubmitDate;

// Calculates the total count of steps
export const totalStepsSelector = createSelector(allStepListSelector, (allStepsList) => allStepsList.length);

// Gets the key (string) of the current step
export const currentStepKeySelector = createSelector(
    allStepListSelector,
    currentStepIndexSelector,
    (allStepsList, stepIndex) => allStepsList[stepIndex],
);

// Combines total steps, current step key and current step index into one selector
export const stepperSelector = createSelector(
    totalStepsSelector,
    currentStepKeySelector,
    currentStepIndexSelector,
    (totalSteps, currentStepKey, currentStepIndex) => ({
        totalSteps,
        currentStepKey,
        currentStepIndex,
    }),
);

// Calculates the current subStep count and index in the substeps
export const subStepsSelector = createSelector(
    (state: RootState) => state.stepper,
    currentStepKeySelector,
    (state, curr) => {
        const { allStepsList, stepIndex } = state;

        let before = allStepsList[stepIndex];

        let subStepsLength = 0;
        let subStepIndex = 0;

        // Adds current and previous steps
        while (curr === before) {
            if (stepIndex - subStepsLength < 0) break;

            subStepsLength++;
            before = allStepsList[stepIndex - subStepsLength];
        }
        subStepIndex = subStepsLength;

        // Adds higher steps
        let after = allStepsList[stepIndex + 1];
        let afterSubStepsLength = 0;
        while (curr === after) {
            if (stepIndex + 1 + afterSubStepsLength > allStepsList.length) break;

            afterSubStepsLength++;
            after = allStepsList[stepIndex + 1 + afterSubStepsLength];
        }

        subStepsLength += afterSubStepsLength;

        return { subStepIndex, subStepsLength };
    },
);

//Calculates what user changed in the change pension contribution flow
export const pensionContributionChangeSelector = createSelector(
    changeContributionStateSelector,
    (changeContributionState) => {
        const currentContribution = changeContributionState?.currentMonthlyContribution;
        const newContribution = changeContributionState?.newMonthlyContribution;

        return {
            contributionChanged:
                currentContribution !== undefined &&
                newContribution !== undefined &&
                Number(currentContribution) !== Number(newContribution),
            employerContributeChanged:
                Boolean(changeContributionState?.currentEmployerContribute) !==
                Boolean(changeContributionState?.newEmployerContribute),
        };
    },
);

//Get index of a step by its name
export const stepIndexByNameSelector = createSelector(
    allStepListSelector,
    (_: RootState, stepName: StepperIndexes) => stepName,
    (allStepsList, stepName) => {
        return allStepsList.findIndex((step) => step === stepName);
    },
);

// Returns true if the questionnaire step is present in the current stepper flow
export const hasQuestionnaireSelector = createSelector(allStepListSelector, (allStepsList) =>
    allStepsList.includes(StepperIndexes.questionnairePage),
);
